/**
 * ExpertsPage — /experts (spec 017, US-14-GR-16 — Expert Consultation Surfacing, admin half)
 *
 * Two responsibilities on one page (FR-042, SC-009):
 *   1. Expert pool management — add / deactivate experts with domains. No hard delete;
 *      deactivate is the only removal path, matching NotificationTemplatesController's
 *      deactivate-only convention for the same reason (referential integrity — an
 *      ExpertConsultation may already reference the row).
 *   2. Consultation queue — assign an expert to a Requested consultation, then complete
 *      it with outcome notes once InProgress. Every consultation must reach a terminal
 *      state (Completed or Cancelled) with an outcome recorded (SC-009) — Complete is
 *      the only path that records notes, so it's surfaced directly on the queue row
 *      rather than buried behind a detail view.
 *
 * An empty pool is rendered as a banner on the queue side, not silently — assigning is
 * impossible with zero active experts, so the queue must say so rather than just
 * showing a dead "Assign" control (FR-042 mirrors this on the user side via poolEmpty).
 */

import { useCallback, useEffect, useState, startTransition, type FormEvent } from 'react';
import { AlertCircle, Check, Clock, Plus, PowerOff, RefreshCw, UserCog, Users, X } from 'lucide-react';
import {
  assignConsultation,
  completeConsultation,
  createExpert,
  deactivateExpert,
  getConsultations,
  getExperts,
  ConsultationStatus,
  CONSULTATION_STATUS_LABELS,
  type ConsultationRecord,
  type ExpertRecord,
} from '../services/expertConsultationAdminService';
import { ageInDays, sortByRequestedAtAscending } from '../utils/expertConsultationBacklog';

// String enum — Object.values() has no numeric-enum reverse-mapping pollution to filter out.
const STATUS_FILTER_OPTIONS = Object.values(ConsultationStatus);

const STATUS_BADGE_CLASS: Record<ConsultationStatus, string> = {
  [ConsultationStatus.Requested]: 'text-zinc-300 bg-zinc-800 border-zinc-700',
  [ConsultationStatus.Assigned]: 'text-blue-300 bg-blue-500/10 border-blue-500/30',
  [ConsultationStatus.InProgress]: 'text-violet-300 bg-violet-500/10 border-violet-500/30',
  [ConsultationStatus.Completed]: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
  [ConsultationStatus.Cancelled]: 'text-zinc-500 bg-zinc-900 border-zinc-800',
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Add-expert form
// ---------------------------------------------------------------------------
function AddExpertForm({
  onSave,
  onCancel,
  saving,
}: {
  onSave: (input: { name: string; email: string; domains: string[] }) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [domainsText, setDomainsText] = useState('');

  const domains = domainsText
    .split(',')
    .map((d) => d.trim())
    .filter((d) => d.length > 0);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || domains.length === 0) return;
    await onSave({ name: name.trim(), email: email.trim(), domains });
  };

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="space-y-3 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="expert-name" className="block text-xs font-medium text-zinc-400 mb-1">
            Name *
          </label>
          <input
            id="expert-name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-sm text-zinc-200 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500"
          />
        </div>
        <div>
          <label htmlFor="expert-email" className="block text-xs font-medium text-zinc-400 mb-1">
            Email *
          </label>
          <input
            id="expert-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-sm text-zinc-200 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500"
          />
        </div>
      </div>
      <div>
        <label htmlFor="expert-domains" className="block text-xs font-medium text-zinc-400 mb-1">
          Domains * <span className="text-zinc-600">(comma-separated, e.g. Marketing, Finance)</span>
        </label>
        <input
          id="expert-domains"
          type="text"
          required
          value={domainsText}
          onChange={(e) => setDomainsText(e.target.value)}
          placeholder="Marketing, Sales"
          className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-sm text-zinc-200 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500"
        />
      </div>
      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={saving || !name.trim() || !email.trim() || domains.length === 0}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 text-zinc-950 text-sm font-medium hover:bg-violet-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]"
        >
          {saving && <RefreshCw className="w-4 h-4 animate-spin" />}
          {saving ? 'Saving…' : 'Save expert'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-lg bg-zinc-700 text-zinc-300 text-sm font-medium hover:bg-zinc-600 transition-colors min-h-[48px]"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Expert pool section
// ---------------------------------------------------------------------------
function ExpertPoolSection({
  experts,
  loading,
  onAdd,
  onDeactivate,
  showForm,
  onShowFormChange,
}: {
  experts: ExpertRecord[];
  loading: boolean;
  onAdd: (input: { name: string; email: string; domains: string[] }) => Promise<void>;
  onDeactivate: (expert: ExpertRecord) => Promise<void>;
  /** Lifted to the page so the empty-pool banner's "Add an expert" button (AC3,
   * ADO 15202) can open this same form instead of a second one. */
  showForm: boolean;
  onShowFormChange: (show: boolean) => void;
}) {
  const setShowForm = onShowFormChange;
  const [saving, setSaving] = useState(false);
  const [deactivatingId, setDeactivatingId] = useState<string | null>(null);

  const handleAdd = async (input: { name: string; email: string; domains: string[] }) => {
    setSaving(true);
    try {
      await onAdd(input);
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (expert: ExpertRecord) => {
    setDeactivatingId(expert.expertId);
    try {
      await onDeactivate(expert);
    } finally {
      setDeactivatingId(null);
    }
  };

  return (
    <section id="expert-pool-section" aria-label="Expert pool">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-violet-400" />
          <h2 className="text-sm font-semibold text-zinc-100">Expert pool</h2>
          {!loading && <span className="text-xs text-zinc-500">({experts.length})</span>}
        </div>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          disabled={showForm}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-violet-600 text-zinc-950 hover:bg-violet-500 transition-colors disabled:opacity-50 min-h-[48px]"
        >
          <Plus className="w-3.5 h-3.5" />
          Add expert
        </button>
      </div>

      {showForm && (
        <div className="mb-3">
          <AddExpertForm onSave={handleAdd} onCancel={() => setShowForm(false)} saving={saving} />
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center h-24">
          <RefreshCw className="w-5 h-5 text-zinc-500 animate-spin" />
        </div>
      )}

      {!loading && experts.length === 0 && !showForm && (
        <div className="text-center py-8 border border-zinc-800 rounded-lg" data-testid="expert-pool-empty-admin">
          <Users className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
          <p className="text-sm text-zinc-500">
            No experts in the pool yet — user requests will explain the wait and offer SBDC/SCORE instead.
          </p>
        </div>
      )}

      {!loading && experts.length > 0 && (
        <ul className="space-y-2">
          {experts.map((expert) => (
            <li
              key={expert.expertId}
              className={`flex items-start gap-4 p-3 rounded-lg border ${
                expert.isActive ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-800 bg-zinc-900/50 opacity-60'
              }`}
              data-testid={`expert-${expert.expertId}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-zinc-200">{expert.name}</span>
                  {!expert.isActive && (
                    <span className="px-1.5 py-0.5 text-xs rounded border border-zinc-800 text-zinc-600">
                      Inactive
                    </span>
                  )}
                </div>
                <p className="text-xs text-zinc-500">{expert.email}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {expert.domains.map((d) => (
                    <span key={d} className="px-1.5 py-0.5 text-[11px] rounded border border-zinc-700 text-zinc-400">
                      {d}
                    </span>
                  ))}
                </div>
              </div>
              {expert.isActive && (
                <button
                  type="button"
                  onClick={() => void handleDeactivate(expert)}
                  disabled={deactivatingId === expert.expertId}
                  aria-label={`Deactivate ${expert.name}`}
                  className="p-1.5 text-zinc-400 hover:text-red-400 transition-colors rounded-lg hover:bg-zinc-700 disabled:opacity-50 min-h-[48px] min-w-[48px]"
                >
                  {deactivatingId === expert.expertId ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <PowerOff className="w-4 h-4" />
                  )}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Assign control — inline expert picker for a Requested consultation
// ---------------------------------------------------------------------------
function AssignControl({
  consultation,
  activeExperts,
  onAssign,
}: {
  consultation: ConsultationRecord;
  activeExperts: ExpertRecord[];
  onAssign: (consultationId: string, expertId: string) => Promise<void>;
}) {
  const [expertId, setExpertId] = useState('');
  const [assigning, setAssigning] = useState(false);

  if (activeExperts.length === 0) {
    return <p className="text-xs text-amber-400">No active experts to assign — add one to the pool above.</p>;
  }

  const handleAssign = async () => {
    if (!expertId) return;
    setAssigning(true);
    try {
      await onAssign(consultation.id, expertId);
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <select
        aria-label={`Assign expert to ${consultation.businessDomain} request`}
        value={expertId}
        onChange={(e) => setExpertId(e.target.value)}
        className="px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-sm text-zinc-200 focus:outline-none focus:ring-2 focus:ring-violet-500/50 min-h-[48px]"
      >
        <option value="">Select expert…</option>
        {activeExperts.map((e) => (
          <option key={e.expertId} value={e.expertId}>
            {e.name}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => void handleAssign()}
        disabled={!expertId || assigning}
        className="px-3 py-1.5 text-xs rounded-lg bg-violet-600 text-zinc-950 hover:bg-violet-500 transition-colors disabled:opacity-50 min-h-[48px]"
      >
        {assigning ? 'Assigning…' : 'Assign'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Complete control — outcome notes, required (SC-009: every consultation must
// reach a terminal state WITH an outcome recorded).
// ---------------------------------------------------------------------------
function CompleteControl({
  consultation,
  onComplete,
}: {
  consultation: ConsultationRecord;
  onComplete: (consultationId: string, notes: string) => Promise<void>;
}) {
  const [notes, setNotes] = useState('');
  const [completing, setCompleting] = useState(false);
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="px-3 py-1.5 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 transition-colors min-h-[48px]"
      >
        Complete
      </button>
    );
  }

  const handleComplete = async () => {
    if (!notes.trim()) return;
    setCompleting(true);
    try {
      await onComplete(consultation.id, notes.trim());
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div className="space-y-2">
      <label htmlFor={`notes-${consultation.id}`} className="sr-only">
        Outcome notes
      </label>
      <textarea
        id={`notes-${consultation.id}`}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={2}
        placeholder="Outcome notes (required)…"
        className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-sm text-zinc-200 focus:outline-none focus:ring-2 focus:ring-violet-500/50"
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void handleComplete()}
          disabled={!notes.trim() || completing}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 transition-colors disabled:opacity-50 min-h-[48px]"
        >
          {completing ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          Mark complete
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="px-3 py-1.5 text-xs rounded-lg bg-zinc-700 text-zinc-300 hover:bg-zinc-600 transition-colors min-h-[48px]"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Requested backlog summary (AC1, ADO 15202) — count + oldest age in days for
// Requested-status consultations, independent of whatever status the queue
// filter below is set to, so the backlog stays visible even when an admin has
// filtered the queue down to e.g. Completed.
// ---------------------------------------------------------------------------
function RequestedBacklogSummary({
  consultations,
  loading,
}: {
  consultations: ConsultationRecord[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-lg border border-zinc-800 bg-zinc-900 text-xs text-zinc-500">
        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
        Loading requested backlog…
      </div>
    );
  }

  const sorted = sortByRequestedAtAscending(consultations);
  const count = sorted.length;

  if (count === 0) {
    return (
      <div
        className="p-3 rounded-lg border border-zinc-800 bg-zinc-900 text-xs text-zinc-500"
        data-testid="requested-backlog-summary"
      >
        No consultations are currently awaiting assignment.
      </div>
    );
  }

  const oldest = sorted[0];
  const oldestDays = ageInDays(oldest.requestedAt);
  // Purely a visual cue for a backlog that has clearly gone stale — the exact
  // cutoff isn't load-bearing, unlike the per-item overdue math in
  // utils/expertConsultationBacklog.ts (AC2), which drives the sidebar badge.
  const stale = oldestDays >= 7;

  return (
    <div
      className={`p-4 rounded-lg border ${stale ? 'border-amber-500/30 bg-amber-500/5' : 'border-zinc-800 bg-zinc-900'}`}
      data-testid="requested-backlog-summary"
    >
      <div className="flex items-center gap-2">
        <Clock className={`w-4 h-4 flex-shrink-0 ${stale ? 'text-amber-400' : 'text-zinc-400'}`} aria-hidden="true" />
        <p className={`text-sm font-medium ${stale ? 'text-amber-300' : 'text-zinc-200'}`}>
          {count.toLocaleString()} request{count === 1 ? '' : 's'} awaiting assignment
        </p>
      </div>
      <p className="text-xs text-zinc-500 mt-1">
        Oldest is{' '}
        <span className={stale ? 'text-amber-400 font-medium' : 'text-zinc-300 font-medium'}>
          {oldestDays.toLocaleString()} day{oldestDays === 1 ? '' : 's'}
        </span>{' '}
        old ({oldest.businessDomain}, requested {formatDate(oldest.requestedAt)})
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty-pool banner (AC3, ADO 15202) — the queue-side "no active experts to
// assign" note on each Requested row (AssignControl, above) is real but easy to
// miss when it's repeated per-row and the pool section's own note lives below
// the fold. This states it once, plainly, at the top of the page, with a
// button that opens (and scrolls to) the existing Add-expert form rather than
// building a second one.
// ---------------------------------------------------------------------------
function EmptyPoolBanner({ onAddExpert }: { onAddExpert: () => void }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 p-4 rounded-lg border border-amber-500/30 bg-amber-500/5"
      data-testid="expert-pool-empty-banner"
    >
      <Users className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
      <div className="flex-1">
        <p className="text-sm font-medium text-amber-300">The expert pool is empty</p>
        <p className="text-xs text-amber-400/80 mt-1">
          There are no active experts, so nothing can be assigned — Requested consultations will keep
          piling up until at least one expert is added.
        </p>
      </div>
      <button
        type="button"
        onClick={onAddExpert}
        className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-amber-500/20 text-amber-300 border border-amber-500/30 hover:bg-amber-500/30 transition-colors min-h-[48px]"
      >
        <Plus className="w-3.5 h-3.5" />
        Add an expert
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Consultation queue section
// ---------------------------------------------------------------------------
function ConsultationQueueSection({
  consultations,
  loading,
  activeExperts,
  statusFilter,
  onFilterChange,
  onAssign,
  onComplete,
}: {
  consultations: ConsultationRecord[];
  loading: boolean;
  activeExperts: ExpertRecord[];
  statusFilter: ConsultationStatus | '';
  onFilterChange: (status: ConsultationStatus | '') => void;
  onAssign: (consultationId: string, expertId: string) => Promise<void>;
  onComplete: (consultationId: string, notes: string) => Promise<void>;
}) {
  return (
    <section aria-label="Consultation queue">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <UserCog className="w-5 h-5 text-violet-400" />
          <h2 className="text-sm font-semibold text-zinc-100">Consultation queue</h2>
        </div>
        <select
          aria-label="Filter by status"
          value={statusFilter}
          onChange={(e) => onFilterChange(e.target.value === '' ? '' : (e.target.value as ConsultationStatus))}
          className="px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-sm text-zinc-200 focus:outline-none focus:ring-2 focus:ring-violet-500/50"
        >
          <option value="">All statuses</option>
          {STATUS_FILTER_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {CONSULTATION_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-24">
          <RefreshCw className="w-5 h-5 text-zinc-500 animate-spin" />
        </div>
      )}

      {!loading && consultations.length === 0 && (
        <div className="text-center py-8 border border-zinc-800 rounded-lg">
          <p className="text-sm text-zinc-500">No consultation requests match this filter.</p>
        </div>
      )}

      {!loading && consultations.length > 0 && (
        <ul className="space-y-2">
          {/* Oldest first (AC1, ADO 15202) — applies across the whole queue, not just
              while filtered to Requested, so "oldest first" holds regardless of filter. */}
          {sortByRequestedAtAscending(consultations).map((c) => (
            <li
              key={c.id}
              className="p-3 rounded-lg border border-zinc-700 bg-zinc-800/30 space-y-2"
              data-testid={`admin-consultation-${c.id}`}
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <span className="text-sm font-medium text-zinc-200">{c.businessDomain}</span>
                <span
                  className={`px-2 py-0.5 rounded-full border text-xs font-medium ${STATUS_BADGE_CLASS[c.status]}`}
                >
                  {CONSULTATION_STATUS_LABELS[c.status]}
                </span>
              </div>
              <p className="text-xs text-zinc-500">
                Requested {formatDate(c.requestedAt)}
                {c.status === ConsultationStatus.Requested &&
                  ` · ${ageInDays(c.requestedAt)} day${ageInDays(c.requestedAt) === 1 ? '' : 's'} old`}
              </p>
              {c.additionalContext && <p className="text-sm text-zinc-400">{c.additionalContext}</p>}
              {c.expertName &&
                (c.status === ConsultationStatus.Assigned || c.status === ConsultationStatus.InProgress) && (
                  <p className="text-xs text-zinc-400">Assigned to {c.expertName}</p>
                )}

              {c.status === ConsultationStatus.Requested && (
                <AssignControl consultation={c} activeExperts={activeExperts} onAssign={onAssign} />
              )}
              {(c.status === ConsultationStatus.Assigned || c.status === ConsultationStatus.InProgress) && (
                <CompleteControl consultation={c} onComplete={onComplete} />
              )}
              {c.status === ConsultationStatus.Completed && c.notes && (
                <p className="text-sm text-emerald-300 bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-2">
                  {c.notes}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export function ExpertsPage() {
  const [experts, setExperts] = useState<ExpertRecord[]>([]);
  const [expertsLoading, setExpertsLoading] = useState(true);
  // Real GetExpertsResponse.PoolEmpty (ACTIVE experts only, per
  // ExpertConsultationContracts.cs) — distinct from `experts.length === 0`,
  // which the pool-section's own inline note below still uses (an
  // inactive-only pool renders there, not here).
  const [poolEmpty, setPoolEmpty] = useState(false);
  const [consultations, setConsultations] = useState<ConsultationRecord[]>([]);
  const [consultationsLoading, setConsultationsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<ConsultationStatus | ''>('');
  const [error, setError] = useState<string | null>(null);
  // Add-expert form visibility, lifted out of ExpertPoolSection (AC3, ADO
  // 15202) so the empty-pool banner's button can open the same form.
  const [showAddExpertForm, setShowAddExpertForm] = useState(false);

  // Requested-status backlog (AC1, ADO 15202) — loaded independent of
  // `statusFilter` so the summary stays visible even when the queue below is
  // filtered to a different status.
  const [backlog, setBacklog] = useState<ConsultationRecord[]>([]);
  const [backlogLoading, setBacklogLoading] = useState(true);

  const loadExperts = useCallback(async () => {
    setExpertsLoading(true);
    try {
      const { experts: list, poolEmpty: empty } = await getExperts();
      setExperts(list);
      setPoolEmpty(empty);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the expert pool');
    } finally {
      setExpertsLoading(false);
    }
  }, []);

  const loadConsultations = useCallback(async () => {
    setConsultationsLoading(true);
    try {
      const list = await getConsultations(statusFilter === '' ? {} : { status: statusFilter });
      setConsultations(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the consultation queue');
    } finally {
      setConsultationsLoading(false);
    }
  }, [statusFilter]);

  const loadBacklog = useCallback(async () => {
    setBacklogLoading(true);
    try {
      const list = await getConsultations({ status: ConsultationStatus.Requested });
      setBacklog(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the requested-consultation backlog');
    } finally {
      setBacklogLoading(false);
    }
  }, []);

  useEffect(() => {
    startTransition(() => {
      void loadExperts();
    });
  }, [loadExperts]);

  useEffect(() => {
    startTransition(() => {
      void loadConsultations();
    });
  }, [loadConsultations]);

  useEffect(() => {
    startTransition(() => {
      void loadBacklog();
    });
  }, [loadBacklog]);

  const handleOpenAddExpertForm = useCallback(() => {
    setShowAddExpertForm(true);
    // Optional chaining on the method itself, not just the element — jsdom (test
    // environment) doesn't implement scrollIntoView at all.
    document.getElementById('expert-pool-section')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleAddExpert = useCallback(
    async (input: { name: string; email: string; domains: string[] }) => {
      setError(null);
      try {
        const created = await createExpert(input);
        setExperts((prev) => [created, ...prev]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add expert');
      }
    },
    [],
  );

  const handleDeactivateExpert = useCallback(async (expert: ExpertRecord) => {
    setError(null);
    try {
      const updated = await deactivateExpert(expert);
      setExperts((prev) => prev.map((e) => (e.expertId === expert.expertId ? updated : e)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate expert');
    }
  }, []);

  const handleAssign = useCallback(async (consultationId: string, expertId: string) => {
    setError(null);
    try {
      const updated = await assignConsultation(consultationId, expertId);
      setConsultations((prev) => prev.map((c) => (c.id === consultationId ? updated : c)));
      // Assigning moves it off Requested — drop it from the backlog summary
      // immediately rather than waiting on the next poll/reload.
      setBacklog((prev) => prev.filter((c) => c.id !== consultationId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign expert');
    }
  }, []);

  const handleComplete = useCallback(async (consultationId: string, notes: string) => {
    setError(null);
    try {
      const updated = await completeConsultation(consultationId, notes);
      setConsultations((prev) => prev.map((c) => (c.id === consultationId ? updated : c)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete consultation');
    }
  }, []);

  const activeExperts = experts.filter((e) => e.isActive);

  return (
    <main className="flex-1 p-6 overflow-y-auto space-y-8">
      <div>
        <h1 className="text-lg font-semibold text-zinc-100">Expert Consultations</h1>
        <p className="text-xs text-zinc-500 mt-0.5">
          Manage the expert pool and drive requests from assignment through to a recorded outcome.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-400 flex-1">{error}</p>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-zinc-500 hover:text-zinc-300"
            aria-label="Dismiss error"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {!expertsLoading && poolEmpty && <EmptyPoolBanner onAddExpert={handleOpenAddExpertForm} />}

      <RequestedBacklogSummary consultations={backlog} loading={backlogLoading} />

      <ExpertPoolSection
        experts={experts}
        loading={expertsLoading}
        onAdd={handleAddExpert}
        onDeactivate={handleDeactivateExpert}
        showForm={showAddExpertForm}
        onShowFormChange={setShowAddExpertForm}
      />

      <ConsultationQueueSection
        consultations={consultations}
        loading={consultationsLoading}
        activeExperts={activeExperts}
        statusFilter={statusFilter}
        onFilterChange={setStatusFilter}
        onAssign={handleAssign}
        onComplete={handleComplete}
      />
    </main>
  );
}

export default ExpertsPage;
